import React, { useState, useEffect } from "react";
import {
  Container,
  Button,
  Box,
  TextField,
  CircularProgress,
  Table,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  InputAdornment,
  Tooltip,
  Divider,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Delete as DeleteIcon, Edit as EditIcon, Info as InfoIcon } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function ClassType() {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [classTypes, setClassTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClassType, setSelectedClassType] = useState({
    classTypeId: null,
    classType: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [permissions, setPermissions] = useState({
    uRead: 0,
    uWrite: 0,
    uModify: 0,
    uDelete: 0,
    isPermission: 0,
  });
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const navigate = useNavigate();

  // Fetch form ID from Form_Authorizer API
  const fetchFormId = async () => {
    try {
      const apiUrl = "https://wsms.aidigitalsec.com";
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/api/ExclusivePermission/Form_Authorizer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ moduleId: 7 }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const classTypeForm = data.find(
          (form) => form.formName === "Class Type"
        );
        if (classTypeForm) {
          return classTypeForm.formId;
        } else {
          console.error("Class Type form not found");
          return null;
        }
      } else {
        console.error("Failed to fetch form ID");
        return null;
      }
    } catch (error) {
      console.error("API request error:", error);
      return null;
    }
  };

  // Fetch permissions from Module_Authorizer API
  const fetchPermissions = async (formId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          return {
            uRead: data[0].uRead,
            uWrite: data[0].uWrite,
            uModify: data[0].uModify,
            uDelete: data[0].uDelete,
            isPermission: data[0].isPermission,
          };
        } else {
          return {
            uRead: 0,
            uWrite: 0,
            uModify: 0,
            uDelete: 0,
            isPermission: 0,
          };
        }
      } else {
        console.error("Failed to fetch permissions");
        return {
          uRead: 0,
          uWrite: 0,
          uModify: 0,
          uDelete: 0,
          isPermission: 0,
        };
      }
    } catch (error) {
      console.error("API request error:", error);
      return {
        uRead: 0,
        uWrite: 0,
        uModify: 0,
        uDelete: 0,
        isPermission: 0,
      };
    }
  };

  // Fetch class types from API and set the state
  const fetchClassTypes = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/ClassType/GetClassType`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        setLoadingBarProgress(100);
        const data = await response.json();
        setClassTypes(data);
      } else {
        setLoadingBarProgress(0);
        console.error("Failed to fetch class types");
      }
    } catch (error) {
      console.error("API request error:", error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const formId = await fetchFormId();
      if (formId) {
        const fetchedPermissions = await fetchPermissions(formId);
        setPermissions(fetchedPermissions);
        if (fetchedPermissions.isPermission) {
          await fetchClassTypes();
        }
      }
      setIsLoading(false);
    };
    initialize();
  }, []);

  const handleAddClassType = () => {
    if (permissions.uWrite) {
      navigate("/addclasstype");
    } else {
      // alert("You are not authorised to add class types");
      setSnackbarMessage('You are not authorised to add class types');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
    }
  };

  const handleShow = (classTypeId, classType) => {
    if (permissions.uDelete) {
      setSelectedClassType({ classTypeId, classType });
      setShowModal(true);
    } else {
      // alert("You are not authorised to delete class types");
      setSnackbarMessage('You are not authorised to delete class types');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
    }
  };

  const handleClose = () => {
    setSelectedClassType({ classTypeId: null, classType: "" });
    setShowModal(false);
  };

  const handleDelete = async () => {
    if (!permissions.uDelete) {
      // alert("You are not authorised to delete class types");
      setSnackbarMessage('You are not authorised to delete class types');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/ClassType/Id?Id=${selectedClassType.classTypeId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        fetchClassTypes();
        setShowModal(false);
        setLoadingBarProgress(100);
        setSnackbarMessage('Class Type deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        setLoadingBarProgress(0);
        console.error("Delete failed");
        // alert("Failed to delete class type");
        setSnackbarMessage('Failed to delete class type');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("API request error:", error);
      alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  if (isLoading) {
    return (
      <Container>
        <Typography variant="h6">Loading...</Typography>
      </Container>
    );
  }

  if (!permissions.isPermission) {
    return (
      <Container>
        <Typography variant="h6" color="error">
          You are not authorised to view this page
        </Typography>
      </Container>
    );
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };

  const filteredData = classTypes.filter((classType) =>
    classType.classType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "The Class Type page displays all configured class types in the system. It allows authorized staff to view, search, add, edit, and delete class types based on their access permissions.",
    },
    {
      title: "🔍 Search",
      content:
        "Use the search box to instantly filter class types by name. The table updates in real time as you type.",
    },
    {
      title: "📊 Table Columns",
      items: [
        "Class Type – The name of the class type (e.g., Regular, Special, Vocational).",
        "Action – Edit or Delete buttons based on your access rights.",
      ],
    },
    {
      title: "⚙️ Actions",
      items: [
        "ADD Class Type – Navigates to the form for creating a new class type. Requires Add permission.",
        "EDIT – Opens the edit form for the selected class type. Requires Edit permission.",
        "DELETE – Permanently removes the class type after confirmation. Requires Delete permission.",
      ],
    },
    {
      title: "🔐 Permissions",
      items: [
        "View – Required to see the class types list.",
        "Add – Required to create new class types.",
        "Edit – Required to modify existing class types.",
        "Delete – Required to remove class types.",
        "If you lack a permission, the corresponding button will be disabled and a warning message will appear.",
      ],
    },
    {
      title: "📄 Pagination",
      content:
        "Use the Rows per page selector to control how many records appear at once (5, 10, 25, or 50). Use the Prev and Next buttons to navigate between pages.",
    },
  ];

  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Class Type
          </Typography>

          {/* Info Icon Button */}
          <Tooltip title="View page information & guide" arrow>
            <IconButton
              onClick={() => setShowInfoModal(true)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.75)",
                backgroundColor: "rgba(255,255,255,0.1)",
                border: "1.5px solid rgba(255,255,255,0.25)",
                borderRadius: "50%",
                width: 30,
                height: 30,
                ml: 0.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(100,181,246,0.25)",
                  borderColor: "#64B5F6",
                  color: "#64B5F6",
                  transform: "scale(1.1)",
                },
              }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Paper
        elevation={3}
        style={{ padding: 16, width: "100%", margin: "auto", marginTop: 6 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
            gap: "10px",
            justifyContent: 'space-between'
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: { padding: "0px 8px" },
            }}
            sx={{
              width: 300,
              backgroundColor: "#f9f9f9",
            }}
          />
          <Tooltip title={permissions.uWrite ? "" : "You are not Authorized to Add"} arrow>
            <span><Button
              variant="contained"
              color="primary"
              onClick={handleAddClassType}
              style={{
                width: "160px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}
              disabled={!permissions.uWrite}
            >
              ADD Class Type
            </Button></span>
          </Tooltip>
        </div>

        {permissions.uRead ? (
          <TableContainer>
            <Table
              sx={{
                minWidth: 650,
                border: '1px solid #ccc',
                '& .MuiTableCell-root': {
                  borderRight: '1px solid #ccc', // vertical line
                  fontSize: '1rem', // increased font size
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell><strong>Class Type</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((filteredClassType) => (
                    <TableRow key={filteredClassType.classTypeId}>
                      <TableCell>{filteredClassType.classType}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        <Tooltip
                          title={
                            permissions.uModify
                              ? "Edit Class Type"
                              : "You are not authorised to edit"
                          }
                          arrow
                        >
                          <span>
                            <Button
                              variant="contained"
                              color="warning"
                              style={{ marginLeft: '10px' }}
                              onClick={() => {
                                if (permissions.uModify) {
                                  navigate(`/editclasstype/${filteredClassType.classTypeId}`);
                                } else {
                                  setSnackbarMessage("You are not authorised to edit class types");
                                  setSnackbarSeverity("error");
                                  setOpenSnackbar(true);
                                }
                              }}
                              disabled={!permissions.uModify}
                              startIcon={<EditIcon />}
                            >
                              EDIT
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            permissions.uDelete
                              ? "Delete Class Type"
                              : "You are not authorised to delete"
                          }
                          arrow
                        >
                          <span>
                            <Button
                              className='mx-2'
                              variant="contained"
                              color="error"
                              onClick={() =>
                                handleShow(
                                  filteredClassType.classTypeId,
                                  filteredClassType.classType
                                )
                              }
                              disabled={!permissions.uDelete}
                              startIcon={<DeleteIcon />}
                            >
                              DELETE
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No Class Types Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>

          </TableContainer>

        ) : (
          <Typography variant="h6" color="error">
            You are not authorised to view class types
          </Typography>
        )}

        {filteredData.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 150 }}
              SelectProps={{
                native: true,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 520 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(11,31,61,0.22)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#64B5F6', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                Class Type — Page Guide
              </Typography>
            </Box>
            {/* Close Button */}
         
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D',
                    mb: 0.8,
                    fontSize: '0.97rem',
                  }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.875rem' }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#444',
                          lineHeight: 1.75,
                          mb: 0.3,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: 'rgba(11,31,61,0.08)' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: '12px 24px',
              borderTop: '1px solid rgba(11,31,61,0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: '#0B1F3D',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a3560' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={showModal} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedClassType?.classType}</strong>?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDelete(handleDelete)}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
}

export default ClassType;